import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import Popup from "reactjs-popup";
import Tippy from "@tippyjs/react";

import UserMenu from '../../UserMenu/components/UserMenu';
import { Conductor } from '@opendate-app/setlist';
import useConductorWidgetHint from '../useConductorWidgetHint';
import ConductorWidgetHint from './ConductorWidgetHint';
import CrmNavIcon from '../../shared/Icons/CrmNavIcon';
import OpendateLogo from '../../shared/Icons/OpendateLogo';
import SeriesNavIcon from '../../shared/Icons/SeriesNavIcon';
import CollectionsNavIcon from '../../shared/Icons/CollectionsNavIcon';
import UnreadDot from '../../shared/components/UnreadDot';

const axios = require('axios').default;

const popupContentStyle = {
  "background": "var(--od-c-fafafa, #fafafa)",
  "border": "1px solid var(--od-border-2, #E9EAEB)",
  "borderRadius": "12px",
  "boxShadow": "0px 12px 16px -4px rgba(10, 13, 18, 0.08), 0px 4px 6px -2px rgba(10, 13, 18, 0.03), 0px 2px 2px -1px rgba(10, 13, 18, 0.04)",
  "width": "320px",
  "padding": "0px",
  "overflowX": "hidden",
  "overflowY": "auto",
  "maxHeight": "calc(100vh - 24px)",
  "zIndex": "20"
}

const AppMenu = ({
  csrfToken,
  currentUser,
  team,
  menuLinks,
  teamMemberships,
  settingsLink,
  helpLink,
  myTicketsLink,
  updatesLink,
  isGuest,
  myEventsPath,
  persistSidebar = true
}) => {
  const getWindowDimensions = () => {
    const { innerWidth: width, innerHeight: height } = (
      typeof(window) !== 'undefined' ? window : { innerWidth: 0, innerHeight: 0 }
    );

    return {
      width,
      height
    };
  }

  const [windowDimensions, setWindowDimensions] = useState(getWindowDimensions());
  const [menuHeight, setMenuHeight] = useState(0);
  const [isMounted, setIsMounted] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasPendingConductorInsight, setHasPendingConductorInsight] = useState(false);
  const [showConductorHint, hideConductorHint] = useConductorWidgetHint();

  useEffect(() => {
    if (!team.package?.ai_features || isGuest) {
      return undefined;
    }

    axios.get('/conductor/venue_chat/insights', { headers: { Accept: 'application/json' } })
      .then((response) => {
        const insights = response.data?.insights || [];
        setHasPendingConductorInsight(insights.some((insight) => insight.status === 'pending'));
      })
      .catch(() => {});

    const handleInsightsChanged = (event) => {
      setHasPendingConductorInsight((event?.detail?.pendingCount || 0) > 0);
    };
    window.addEventListener('conductorInsightsChanged', handleInsightsChanged);
    return () => window.removeEventListener('conductorInsightsChanged', handleInsightsChanged);
  }, [team.package?.ai_features, isGuest]);

  useEffect(() => {
    setWindowDimensions(getWindowDimensions());

    var handleResize = () => {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMenuHeight(windowDimensions.height - 8);
  }, [windowDimensions]);

  useEffect(() => {
    setIsMounted(true);
    if (persistSidebar === false) return;
    try {
      if (localStorage.getItem('sidebarExpanded') === 'true') {
        setIsExpanded(true);
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    document.body.classList.toggle('sidebar-expanded', isExpanded);
    if (persistSidebar === false) return;
    try { localStorage.setItem('sidebarExpanded', isExpanded); } catch (e) {}
  }, [isExpanded]);

  const menuItemClass = (active) => {
    return 'd-flex align-items-center position-relative main-menu-link'
      + (isExpanded ? ' justify-content-start' : ' justify-content-center')
      + (active ? ' active' : '');
  };

  const menuItemStyle = {
    "height": "40px",
    "minHeight": "40px",
    "borderRadius": "8px",
    "marginBottom": "5px",
    "paddingLeft": isExpanded ? "10px" : "0px",
    "width": isExpanded ? "100%" : "40px"
  };

  const effectiveMenuLinks = isGuest
    ? [{
        toolTip: "My Events",
        href: myEventsPath,
        active: typeof window !== 'undefined' && window.location.pathname === myEventsPath,
        xlinkHref: "#myEvents"
      }]
    : menuLinks;

  return (
    <div>
      <div className={"card align-self-start app-menu-sidebar" + (isExpanded ? " expanded" : "")}
            style={{
              "border": "1px solid #e9eaeb",
              "marginTop": "4px",
              "width": isExpanded ? "220px" : "62px",
              "height": `${menuHeight}px`,
              "position": "fixed",
              "zIndex": "10"
            }}>
        <div className={"card-body d-flex flex-column" + (isExpanded ? " align-items-start" : " align-items-center")}
              style={{
                "padding": isExpanded ? "20px 12px" : "20px 0px"
              }}>
          {isExpanded ? (
            <div className="d-flex align-items-center justify-content-between w-100"
                  style={{ "marginBottom": "15px", "paddingLeft": "2px" }}>
              <OpendateLogo height={36} />
              <button className="btn btn-link p-0 sidebar-collapse-btn"
                      onClick={() => setIsExpanded(false)}
                      aria-label="Collapse sidebar"
                      style={{ "border": "none", "background": "none" }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M13.3333 11.3333H2.66666M2.66666 11.3333L5.33332 14M2.66666 11.3333L5.33332 8.66667M13.3333 4.66667H5.99999M5.99999 4.66667L8.66666 7.33333M5.99999 4.66667L8.66666 2" stroke="#A4A7AE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
          ) : (
            <div className="sidebar-toggle-wrapper"
                  onClick={() => setIsExpanded(true)}
                  role="button"
                  aria-label="Expand sidebar"
                  style={{ "marginBottom": "15px" }}>
              <img src="/Opendate-mark-512.png"
                    className="img-fluid"
                    style={{
                      "width": "36px",
                      "height": "36px"
                    }} />
              <div className="sidebar-expand-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ transform: "rotate(180deg)" }}>
                  <path d="M13.3333 11.3333H2.66666M2.66666 11.3333L5.33332 14M2.66666 11.3333L5.33332 8.66667M13.3333 4.66667H5.99999M5.99999 4.66667L8.66666 7.33333M5.99999 4.66667L8.66666 2" stroke="#A4A7AE" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>
          )}
          <div className={"flex-grow-1 d-flex flex-column" + (isExpanded ? " align-items-start" : " align-items-center")}
                style={{
                  overflowX: "hidden",
                  overflowY: "auto",
                  scrollbarWidth: "none",
                  width: "100%"
                }}>
            <div className={'flex-grow-1 d-flex flex-column' + (isExpanded ? ' align-items-start' : ' align-items-center')}
                  style={{ width: "100%" }}>
              {effectiveMenuLinks.map((link, index) => (
                <Tippy key={index}
                        placement="right"
                        content={link.toolTip}
                        disabled={isExpanded}>
                  <div className={menuItemClass(link.active)}
                        style={menuItemStyle}>
                    <a href={link.href}
                        className='stretched-link d-flex align-items-center'>
                      <span className="position-relative d-inline-flex">
                        {link.toolTip === 'CRM' ? (
                          <CrmNavIcon />
                        ) : link.toolTip === 'Series' ? (
                          <SeriesNavIcon />
                        ) : link.toolTip === 'Collections' ? (
                          <CollectionsNavIcon />
                        ) : (
                          <svg className="sprite-icon"
                                style={{
                                  "width": "17px",
                                  "height": "17px",
                                  "minWidth": "17px",
                                  "marginTop": "-3px"
                                }}>
                            <use xlinkHref={link.xlinkHref}></use>
                          </svg>
                        )}
                        {link.unread && <UnreadDot label={link.unreadLabel} />}
                      </span>
                      {isExpanded && <span className="menu-label">{link.toolTip}</span>}
                    </a>
                  </div>
                </Tippy>
              ))}
            </div>
            <div className={'d-flex flex-column' + (isExpanded ? ' align-items-start' : ' align-items-center')}
                  style={{ width: "100%" }}>
              {team.package?.ai_features && !isGuest && (
                <Tippy key={showConductorHint ? 'hint' : 'label'}
                       placement="right"
                       {...(showConductorHint
                         ? { content: <ConductorWidgetHint onDismiss={hideConductorHint} />, visible: true, interactive: true, appendTo: () => document.body }
                         : { content: 'Conductor', disabled: isExpanded })}>
                  <div className={'d-flex align-items-center position-relative main-menu-link'
                        + (isExpanded ? ' justify-content-start' : ' justify-content-center align-self-center')}
                        style={{
                          ...menuItemStyle,
                          "cursor": "pointer"
                        }}
                        data-conductor-launcher="true"
                        onClick={() => {
                          hideConductorHint();
                          window.dispatchEvent(new CustomEvent('openConductor', { detail: { source: 'app_menu' } }));
                        }}>
                    <div
                      style={{
                        "position": "relative",
                        "width": "17px",
                        "height": "17px",
                        "minWidth": "17px",
                        "marginTop": "-3px",
                        "display": "flex",
                        "alignItems": "center",
                        "justifyContent": "center"
                      }}>
                      <Conductor className="conductor-nav-icon" width={17} height={17} />
                      {hasPendingConductorInsight && <UnreadDot label="New Conductor insight" />}
                    </div>
                    {isExpanded && <span className="menu-label">Conductor</span>}
                  </div>
                </Tippy>
              )}
              {Object.keys(myTicketsLink).length > 0 ? (
                <Tippy placement="right"
                      content={myTicketsLink.toolTip}
                      disabled={isExpanded}>
                  <div className={menuItemClass(myTicketsLink.active)}
                        style={menuItemStyle}>
                    <a href={myTicketsLink.href}
                        className='stretched-link d-flex align-items-center'>
                      <svg className="sprite-icon"
                            style={{
                              "width": "17px",
                              "height": "17px",
                              "minWidth": "17px",
                              "marginTop": "-3px"
                            }}>
                        <use xlinkHref={myTicketsLink.xlinkHref}></use>
                      </svg>
                      {isExpanded && <span className="menu-label">{myTicketsLink.toolTip}</span>}
                    </a>
                  </div>
                </Tippy>
              ) : null}
              {Object.keys(updatesLink).length > 0 ? (
                <Tippy placement="right"
                        content={updatesLink.toolTip}
                        disabled={isExpanded}>
                  <div className={menuItemClass(updatesLink.active)}
                        style={menuItemStyle}>
                    <a href={updatesLink.href}
                        className='stretched-link d-flex align-items-center'>
                      <span className="position-relative d-inline-flex">
                        <svg className="sprite-icon"
                              style={{
                                "width": "17px",
                                "height": "17px",
                                "minWidth": "17px",
                                "marginTop": "-3px"
                              }}>
                          <use xlinkHref={updatesLink.xlinkHref}></use>
                        </svg>
                        {updatesLink.unread && <UnreadDot />}
                      </span>
                      {isExpanded && <span className="menu-label">{updatesLink.toolTip}</span>}
                    </a>
                  </div>
                </Tippy>
              ) : null}
              {Object.keys(helpLink).length > 0 ? (
                <Tippy placement="right"
                        content={helpLink.toolTip}
                        disabled={isExpanded}>
                  <div className={menuItemClass(helpLink.active)}
                        style={menuItemStyle}>
                    <a href={helpLink.href}
                        className='stretched-link d-flex align-items-center'>
                      <svg className="sprite-icon"
                            style={{
                              "width": "17px",
                              "height": "17px",
                              "minWidth": "17px",
                              "marginTop": "-3px"
                            }}>
                        <use xlinkHref={helpLink.xlinkHref}></use>
                      </svg>
                      {isExpanded && <span className="menu-label">{helpLink.toolTip}</span>}
                    </a>
                  </div>
                </Tippy>
              ) : null}
              {Object.keys(settingsLink).length > 0 ? (
                <Tippy placement="right"
                        content={settingsLink.toolTip}
                        disabled={isExpanded}>
                  <div className={menuItemClass(settingsLink.active)}
                        style={menuItemStyle}>
                    <a href={settingsLink.href}
                        className='stretched-link d-flex align-items-center'>
                      <svg className="sprite-icon"
                            style={{
                              "width": "17px",
                              "height": "17px",
                              "minWidth": "17px",
                              "marginTop": "-3px"
                            }}>
                        <use xlinkHref={settingsLink.xlinkHref}></use>
                      </svg>
                      {isExpanded && <span className="menu-label">{settingsLink.toolTip}</span>}
                    </a>
                  </div>
                </Tippy>
              ) : null}
            </div>
          </div>
          <div style={{"marginTop": "10px"}}>
            <Popup arrow={false}
                  offsetY={0}
                  offsetX={5}
                  position="right bottom"
                  contentStyle={popupContentStyle}
                  onOpen={
                    (e) => {
                      e.preventDefault();
                    }
                  }
                  trigger={open => (
                    <a href="#" className={isExpanded ? "d-flex align-items-center" : ""} style={{ "textDecoration": "none" }}>
                      <div className='position-relative'>
                        <img src={currentUser.avatar_thumb_url}
                              className='img-fluid rounded-circle'
                              style={{"width": "40px", "height": "40px"}} />
                        <div className='rounded-circle position-absolute'
                              style={{
                                "width": "10px",
                                "height": "10px",
                                "background": "#A2DC47",
                                "border": "1.5px solid #ffffff",
                                "bottom": "0px",
                                "right": "0px"
                              }}>
                        </div>
                      </div>
                      {isExpanded && (
                        <div className="ml-2" style={{ "overflow": "hidden" }}>
                          <div style={{ "fontSize": "13px", "fontWeight": "600", "color": "#333", "whiteSpace": "nowrap", "overflow": "hidden", "textOverflow": "ellipsis" }}>
                            {[currentUser.first_name, currentUser.last_name].filter(Boolean).join(' ')}
                          </div>
                          <div style={{ "fontSize": "12px", "color": "#999", "whiteSpace": "nowrap", "overflow": "hidden", "textOverflow": "ellipsis" }}>
                            {currentUser.email}
                          </div>
                        </div>
                      )}
                    </a>
                  )} >
              {close => (
                <UserMenu csrfToken={csrfToken}
                          currentUser={currentUser}
                          team={team}
                          teamMemberships={teamMemberships} />
              )}
            </Popup>
          </div>
        </div>
      </div>
    </div>
  )
};

AppMenu.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  currentUser: PropTypes.object.isRequired,
  team: PropTypes.object.isRequired,
  menuLinks: PropTypes.array.isRequired,
  teamMemberships: PropTypes.array.isRequired,
  settingsLink: PropTypes.object,
  helpLink: PropTypes.object,
  myTicketsLink: PropTypes.object,
  updatesLink: PropTypes.object,
  isGuest: PropTypes.bool,
  myEventsPath: PropTypes.string,
};

export default AppMenu;
